import React, { useCallback, useEffect, useState } from 'react';
import crmClient from './crmClient';
import { formatDate } from './format';

const DispatchAlertsSettings = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    crmClient
      .listDispatchAlertRecipients()
      .then(payload => {
        setRecipients((payload && payload.recipients) || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load dispatch alert recipients.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = (action, after) => {
    setBusy(true);
    setError(null);
    action()
      .then(() => {
        setBusy(false);
        if (after) after();
        load();
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Action failed.');
      });
  };

  const addRecipient = () => {
    if (!newName.trim() || !newPhone.trim()) return;
    run(
      () => crmClient.createDispatchAlertRecipient({ name: newName.trim(), phone: newPhone.trim(), enabled: true }),
      () => {
        setNewName('');
        setNewPhone('');
      }
    );
  };

  return (
    <div className="crm-settings">
      <section className="crm-card">
        <h3>Dispatch SMS alerts</h3>
        <p className="crm-muted">
          These people get a text message from admin dispatch whenever a new order lands. Toggle someone off to take them
          off the on-call rotation without deleting them.
        </p>
        <div className="crm-field-row">
          <label className="crm-field-grow">
            <span>Name</span>
            <input type="text" value={newName} placeholder="Jane Doe" onChange={event => setNewName(event.target.value)} />
          </label>
          <label className="crm-field-grow">
            <span>Phone number</span>
            <input
              type="tel"
              value={newPhone}
              placeholder="(337) 555-0123"
              onChange={event => setNewPhone(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="crm-btn"
            disabled={busy || !newName.trim() || !newPhone.trim()}
            onClick={addRecipient}
          >
            Add recipient
          </button>
        </div>
      </section>

      {error && <div className="crm-inline-error">{error}</div>}

      <section className="crm-card">
        <h3>On-call recipients</h3>
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Receiving alerts</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="crm-empty">
                    Loading recipients…
                  </td>
                </tr>
              )}
              {!loading && recipients.length === 0 && (
                <tr>
                  <td colSpan={5} className="crm-empty">
                    No recipients yet. Add someone above to start sending dispatch alerts.
                  </td>
                </tr>
              )}
              {!loading &&
                recipients.map(recipient => (
                  <tr key={recipient.dispatch_alert_recipient_id}>
                    <td>
                      <strong>{recipient.name || '—'}</strong>
                    </td>
                    <td>{recipient.phone || '—'}</td>
                    <td>
                      <label className="crm-toggle">
                        <input
                          type="checkbox"
                          checked={!!recipient.enabled}
                          disabled={busy}
                          onChange={event =>
                            run(() =>
                              crmClient.updateDispatchAlertRecipient(recipient.dispatch_alert_recipient_id, {
                                enabled: event.target.checked,
                              })
                            )
                          }
                        />
                        <span>{recipient.enabled ? 'On' : 'Off'}</span>
                      </label>
                    </td>
                    <td>{formatDate(recipient.created_at)}</td>
                    <td className="crm-num">
                      <button
                        type="button"
                        className="crm-btn crm-btn--danger crm-btn--sm"
                        disabled={busy}
                        onClick={() => run(() => crmClient.deleteDispatchAlertRecipient(recipient.dispatch_alert_recipient_id))}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default DispatchAlertsSettings;
